import { optimizedImage } from "@/lib/cloudinaryImage";

const FAV_KEY = "naami_fav_counts";

// Device-level favourites for guests: { "brandId:itemId": qty }
export const recordDeviceFavourites = (lines) => {
    try {
        const cur = JSON.parse(localStorage.getItem(FAV_KEY) || "{}");
        for (const l of lines || []) {
            const k = `${l.brand_id}:${l.item_id}`;
            cur[k] = (cur[k] || 0) + (l.qty || 1);
        }
        localStorage.setItem(FAV_KEY, JSON.stringify(cur));
    } catch { /* storage unavailable */ }
};

// Count a basket only once payment succeeds: stash before Stripe, commit on the success page.
const PENDING_KEY = "naami_fav_pending";
export const stashPendingFavourites = (lines) => {
    try { localStorage.setItem(PENDING_KEY, JSON.stringify(lines || [])); } catch { /* ignore */ }
};
export const commitPendingFavourites = () => {
    try {
        const lines = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
        localStorage.removeItem(PENDING_KEY);
        if (lines?.length) recordDeviceFavourites(lines);
    } catch { /* ignore */ }
};

export const readDeviceFavourites = () => {
    try {
        const cur = JSON.parse(localStorage.getItem(FAV_KEY) || "{}");
        return Object.entries(cur)
            .map(([k, count]) => { const [brand_id, item_id] = k.split(":"); return { brand_id, item_id, count }; })
            .sort((a, b) => b.count - a.count);
    } catch {
        return [];
    }
};

// Resolve raw {brand_id, item_id} rows against today's catalogue; drops removed / unavailable items.
export const resolveFavourites = (rows, brands, limit = 6) => {
    const byId = Object.fromEntries((brands || []).map((b) => [b.id, b]));
    const out = [];
    for (const r of rows || []) {
        const brand = byId[r.brand_id];
        const item = brand?.items?.find((i) => i.id === r.item_id && i.available !== false);
        if (brand && item) out.push({ brand, item });
        if (out.length >= limit) break;
    }
    return out;
};

export const FavouritesRail = ({ favourites, onAdd }) => {
    if (!favourites?.length) return null;
    return (
        <div className="mb-6" data-testid="favourites-rail">
            <p className="eyebrow mb-3">// ♥ Your favourites</p>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-5 px-5 snap-x scroll-px-5">
                {favourites.map(({ brand, item }) => (
                    <div
                        key={`${brand.id}:${item.id}`}
                        className="snap-start shrink-0 w-40 border border-[var(--border)] bg-[var(--surface)] flex flex-col"
                        style={{ "--brand-color": brand.accent }}
                        data-testid={`favourite-${item.id}`}
                    >
                        <div className="h-24 bg-[var(--surface-elevated)] overflow-hidden grid place-items-center">
                            {item.image_url ? (
                                <img src={optimizedImage(item.image_url, { w: 200 })} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                                <div className="logo-box" style={{ width: 40, height: 40, fontSize: 14 }}>{brand.token}</div>
                            )}
                        </div>
                        <div className="p-2.5 flex-1 flex flex-col">
                            <span className="font-mono text-[9px] uppercase tracking-[0.16em] truncate" style={{ color: brand.accent }}>{brand.name}</span>
                            <span className="text-[13px] leading-tight mt-0.5 line-clamp-2 flex-1">{item.name}</span>
                            <div className="flex items-center justify-between mt-2">
                                <span className="font-mono text-[12px] text-gold">£{Number(item.price).toFixed(2)}</span>
                                <button
                                    onClick={() => onAdd(brand, item)}
                                    className="w-8 h-8 bg-[var(--gold)] text-[var(--bg)] font-bold grid place-items-center transition-transform active:scale-90"
                                    aria-label={`Add ${item.name}`}
                                    data-testid={`favourite-add-${item.id}`}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
